import { Box, Flex, forwardRef, Popover, StyleProps } from '@chakra-ui/react';
import { Children, PropsWithChildren, ReactElement } from 'react';

import { CalendarOutline } from '@bonusly/ds-icon';
import { rem } from '@bonusly/ds-utils';

import { useMonthRangePicker } from '../../hooks/use-month-range-picker';
import { DateRange } from '../../types';
import { MonthPickerContext } from '../month-picker-context';
import { MonthPicker } from '../month-picker/month-picker';
import { MonthPickerControls } from '../month-picker/month-picker-controls';
import { MonthPickerFooter } from '../month-picker/month-picker-footer';
import { MonthRangeHeader } from '../month-picker/month-range-header';
import { MonthRangeInput } from './month-range-input';
import { MonthRangePopup } from './month-range-popup';

export type MonthRangeSelectorProps = PropsWithChildren<
	StyleProps & {
		disableMonthIf?: (month: Date, interimValue: DateRange) => boolean;
		disabledMonthTooltipText?: string;
		displayFormat?: 'short' | 'long';
		isClearable?: boolean;
		isDisabled?: boolean;
		leftIcon?: ReactElement;
		onChange: (value: DateRange) => void;
		value: DateRange;
	}
>;

export const MonthRangeSelector = forwardRef<MonthRangeSelectorProps, 'button'>(
	(
		{
			children,
			displayFormat = 'short',
			isClearable = true,
			isDisabled = false,
			leftIcon = <CalendarOutline />,
			onChange,
			value,
			disableMonthIf,
			disabledMonthTooltipText,
			...rest
		},
		ref
	) => {
		const picker = useMonthRangePicker({ onChange, value, disableMonthIf });

		return (
			<MonthPickerContext.Provider value={{ displayFormat, isClearable, isDisabled, value, disabledMonthTooltipText, ...picker }}>
				<Popover isLazy isOpen={picker.isOpen} onClose={picker.onClose}>
					<MonthRangeInput leftIcon={leftIcon} ref={ref} {...rest} />

					<MonthRangePopup bg="bg.canvas" borderColor="transparent" p={1} w="unset">
						<Flex columnGap={4}>
							<Box flex={1}>
								<Box
									as="ul"
									listStyleType="none"
									sx={{
										'li:not(:last-child)': {
											mb: 1,
										},
									}}
								>
									{Children.map(Children.toArray(children), (child) => (
										<Box as="li">{child}</Box>
									))}
								</Box>
							</Box>
							<Box bg="bg.surface" borderRadius="sm" boxShadow="sm" p={3} minW={rem(360)}>
								<MonthPickerControls mb={2} />
								<MonthRangeHeader mb={3} />
								<MonthPicker mb={3} />
								<MonthPickerFooter />
							</Box>
						</Flex>
					</MonthRangePopup>
				</Popover>
			</MonthPickerContext.Provider>
		);
	}
);

MonthRangeSelector.displayName = 'MonthRangeSelector';
